import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useAvaliacoes } from '../../api/hooks/useAvaliacoes';
import type { NotaAluno } from '../../api/hooks/useAvaliacoes';
import {
  Award, ArrowLeft, Users, Calendar, FileText, Clipboard,
  PenTool, MessageCircle, RefreshCw,
  TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import { MobilePageWrapper } from '../../components/layout/MobilePageWrapper';

const TIPO_ICONS: Record<string, any> = {
  PROVA: FileText, TRABALHO: Clipboard, ATIVIDADE: PenTool,
  PARTICIPACAO: MessageCircle, RECUPERACAO: RefreshCw,
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  RASCUNHO: { bg: '#f3f4f6', color: '#6b7280' },
  AGENDADA: { bg: '#dbeafe', color: '#1e40af' },
  EM_APLICACAO: { bg: '#fef3c7', color: '#92400e' },
  CONCLUIDA: { bg: '#dcfce7', color: '#166534' },
  CANCELADA: { bg: '#fee2e2', color: '#991b1b' },
};

export function AvaliacaoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { useNotasAvaliacao, avaliacoes } = useAvaliacoes({ professorId: user?.id });
  const { data: notas, isLoading: notasLoading } = useNotasAvaliacao(id);

  // Encontrar a avaliacao na lista (ou null)
  const avaliacao = useMemo(() =>
    avaliacoes.find(a => a.id === id) || null,
    [avaliacoes, id]
  );

  // Estatisticas
  const stats = useMemo(() => {
    if (!notas?.length) return { media: 0, maior: 0, menor: 0, entregaram: 0, faltaram: 0, total: 0 };
    const notasValidas = notas.filter((n: NotaAluno) => n.valorObtido !== null && n.valorObtido !== undefined && !n.faltou);
    const valores = notasValidas.map((n: NotaAluno) => n.valorObtido!);
    const entregaram = notas.filter((n: NotaAluno) => n.entregou).length;
    const faltaram = notas.filter((n: NotaAluno) => n.faltou).length;
    return {
      media: valores.length > 0 ? valores.reduce((a, b) => a + b, 0) / valores.length : 0,
      maior: valores.length > 0 ? Math.max(...valores) : 0,
      menor: valores.length > 0 ? Math.min(...valores) : 0,
      entregaram,
      faltaram,
      total: notas.length,
    };
  }, [notas]);

  const TipoIcon = avaliacao ? (TIPO_ICONS[avaliacao.tipo] || FileText) : FileText;
  const statusColor = avaliacao ? (STATUS_COLORS[avaliacao.status] || STATUS_COLORS.RASCUNHO) : STATUS_COLORS.RASCUNHO;

  if (!id) {
    return (
      <div className="admin-page" style={{ textAlign: 'center', padding: 60 }}>
        <p>Avaliacao nao encontrada</p>
      </div>
    );
  }

  return (
    <MobilePageWrapper title={avaliacao?.titulo || 'Avaliacao'} icon={Award} iconColor="#c4b5fd" backTo="/professor/avaliacoes">
    <div className="admin-page">
      {/* Header com botao voltar */}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/professor/avaliacoes')}
            style={{
              width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)',
              cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s',
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 style={{ fontSize: '1.3rem', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Award size={22} style={{ color: '#8b5cf6' }} />
              {avaliacao?.titulo || 'Carregando...'}
            </h1>
            {avaliacao && (
              <p style={{ opacity: 0.7 }}>
                {avaliacao.turma?.nome} · {avaliacao.materia?.nome} · {avaliacao.bimestre}o Bimestre
              </p>
            )}
          </div>
        </div>
      </div>

      {!avaliacao ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Carregando avaliacao...</div>
      ) : (
        <>
          {/* Info card */}
          <div style={{
            background: 'var(--bg-card, white)', borderRadius: 14, padding: '20px 24px',
            border: '1px solid var(--border-color, #e5e7eb)', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(139,92,246,0.1)', flexShrink: 0,
              }}>
                <TipoIcon size={22} style={{ color: '#8b5cf6' }} />
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{
                    fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                    background: statusColor.bg, color: statusColor.color,
                  }}>
                    {avaliacao.status}
                  </span>
                  <span style={{
                    fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                    background: '#ede9fe', color: '#7c3aed',
                  }}>
                    {avaliacao.tipo}
                  </span>
                  <span style={{
                    fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                    background: '#dbeafe', color: '#1e40af',
                  }}>
                    Peso {avaliacao.peso}x
                  </span>
                </div>
                {avaliacao.descricao && (
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0 0 8px' }}>{avaliacao.descricao}</p>
                )}
                <div style={{ display: 'flex', gap: 14, fontSize: '0.72rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  {avaliacao.dataAplicacao && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Calendar size={12} /> {new Date(avaliacao.dataAplicacao).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Users size={12} /> {stats.total} alunos
                  </span>
                  <span>Valor maximo: {avaliacao.valorMaximo}</span>
                </div>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="kpi-grid kpi-grid-4" style={{ marginBottom: 16 }}>
            {[
              { label: 'Media', value: stats.media.toFixed(1), color: stats.media >= avaliacao.valorMaximo * 0.6 ? '#16a34a' : '#dc2626' },
              { label: 'Maior Nota', value: stats.maior.toFixed(1), color: '#2563eb' },
              { label: 'Menor Nota', value: stats.menor.toFixed(1), color: '#d97706' },
              { label: 'Faltaram', value: String(stats.faltaram), color: stats.faltaram > 0 ? '#dc2626' : '#16a34a' },
            ].map(k => (
              <div className="kpi-card" key={k.label}>
                <div className="kpi-card-info">
                  <h3>{k.label}</h3>
                  <span className="kpi-value" style={{ color: k.color }}>{k.value}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Lista de notas */}
          <div style={{
            background: 'var(--bg-card, white)', borderRadius: 12,
            border: '1px solid var(--border-color, #e5e7eb)', overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid var(--border-color, #e5e7eb)',
              fontWeight: 700, fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between',
            }}>
              <span>Notas dos Alunos</span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                {stats.entregaram} entregaram · {stats.faltaram} faltaram
              </span>
            </div>

            {notasLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando notas...</div>
            ) : !notas?.length ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
                Nenhuma nota lancada ainda
              </div>
            ) : (
              <div>
                {notas.sort((a: NotaAluno, b: NotaAluno) => (a.aluno?.nome || '').localeCompare(b.aluno?.nome || '')).map((nota: NotaAluno, i: number) => {
                  const pct = avaliacao.valorMaximo > 0 && nota.valorObtido != null
                    ? (nota.valorObtido / avaliacao.valorMaximo) * 100
                    : 0;
                  const notaColor = nota.faltou ? '#dc2626' : pct >= 60 ? '#16a34a' : pct >= 40 ? '#d97706' : '#dc2626';
                  const TrendIcon = pct >= 70 ? TrendingUp : pct >= 40 ? Minus : TrendingDown;

                  return (
                    <div key={nota.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                      borderBottom: i < notas.length - 1 ? '1px solid var(--border-color, #f0f0f0)' : 'none',
                    }}>
                      {/* Numero */}
                      <span style={{ width: 24, fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0 }}>{i + 1}</span>

                      {/* Avatar */}
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: nota.faltou ? '#fee2e2' : '#ede9fe', color: nota.faltou ? '#991b1b' : '#7c3aed',
                        fontSize: '0.65rem', fontWeight: 700, flexShrink: 0,
                      }}>
                        {nota.aluno?.nome?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?'}
                      </div>

                      {/* Nome */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          color: nota.faltou ? '#9ca3af' : 'var(--text-main)',
                          textDecoration: nota.faltou ? 'line-through' : 'none',
                        }}>
                          {nota.aluno?.nome || 'Aluno'}
                        </div>
                        {nota.observacao && (
                          <div style={{ fontSize: '0.68rem', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {nota.observacao}
                          </div>
                        )}
                      </div>

                      {/* Nota */}
                      {nota.faltou ? (
                        <span style={{
                          fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                          background: '#fee2e2', color: '#991b1b',
                        }}>
                          FALTOU
                        </span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <TrendIcon size={14} style={{ color: notaColor }} />
                          <span style={{
                            fontSize: '1rem', fontWeight: 800, color: notaColor,
                            minWidth: 40, textAlign: 'right',
                          }}>
                            {nota.valorObtido != null ? nota.valorObtido.toFixed(1) : '-'}
                          </span>
                          <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>/{avaliacao.valorMaximo}</span>
                        </div>
                      )}

                      {/* Barra de progresso */}
                      {!nota.faltou && nota.valorObtido != null && (
                        <div style={{ width: 60, height: 4, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden', flexShrink: 0 }}>
                          <div style={{ height: '100%', borderRadius: 2, background: notaColor, width: `${pct}%`, transition: 'width 0.3s' }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Botao voltar (footer) */}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/professor/avaliacoes')}
              style={{
                padding: '10px 24px', borderRadius: 10, border: '1px solid var(--border-color, #d1d5db)',
                background: 'var(--bg-card, white)', color: 'var(--text-main)', fontSize: '0.85rem',
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              <ArrowLeft size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Voltar para Avaliacoes
            </button>
          </div>
        </>
      )}
    </div>
    </MobilePageWrapper>
  );
}
